/**
 * Supplier Categories Master — admin CRUD.
 * System categories cannot be deleted, only deactivated.
 */
import { useState } from 'react'
import {
  Button, Form, Input, Tag, Space, App, Modal, Popconfirm, Table, Switch,
} from 'antd'
import { PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supplierCategoriesApi } from '@/features/categories/api'
import type { SupplierCategory } from '@/shared/types/categories'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function SupplierCategoriesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<SupplierCategory | null>(null)

  const canCreate = usePermission('supplier.create')
  const canUpdate = usePermission('supplier.update')
  const canManage = canCreate || canUpdate

  const { data = [], isLoading } = useQuery({
    queryKey: ['supplier-categories-all'],
    queryFn: () => supplierCategoriesApi.list(false),
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing
        ? supplierCategoriesApi.update(editing.id, values as Partial<SupplierCategory>)
        : supplierCategoriesApi.create(values as { code: string; name: string }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-categories-all'] })
      qc.invalidateQueries({ queryKey: ['supplier-categories'] })
      message.success(editing ? 'Category updated' : 'Category created')
      setOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      supplierCategoriesApi.update(id, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-categories-all'] })
      message.success('Updated')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => supplierCategoriesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-categories-all'] })
      message.success('Deactivated')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    setOpen(true)
  }

  const openEdit = (r: SupplierCategory) => {
    setEditing(r)
    form.setFieldsValue(r)
    setOpen(true)
  }

  const columns = [
    { title: 'Code', dataIndex: 'code', width: 220, render: (v: string) => <Tag style={{ fontFamily: 'monospace' }}>{v}</Tag> },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Type', dataIndex: 'is_system',
      render: (v: boolean) => v ? <Tag color="blue">System</Tag> : <Tag>Custom</Tag>,
    },
    {
      title: 'Status', dataIndex: 'is_active',
      render: (v: boolean) => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, r: SupplierCategory) => (
        <Space>
          {canManage && <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>}
          {canManage && (
            <Button
              size="small"
              icon={r.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              danger={r.is_active}
              onClick={() => toggleMutation.mutate({ id: r.id, is_active: !r.is_active })}
              disabled={r.is_system && r.is_active}
            >
              {r.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Supplier / Vendor Categories"
        subtitle="System categories are built-in and cannot be deleted. Create custom ones as needed."
        extra={canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Category</Button>
        )}
      />
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        size="middle"
        pagination={false}
      />

      <Modal
        title={editing ? 'Edit Category' : 'New Category'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then(v => saveMutation.mutate(v))}
        confirmLoading={saveMutation.isPending}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editing && (
            <Form.Item name="code" label="Code" rules={[{ required: true, pattern: /^[A-Z0-9_]+$/, message: 'Uppercase + underscores only' }]}>
              <Input placeholder="e.g. FOREIGN_AGENT" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
            </Form.Item>
          )}
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Foreign Agent" />
          </Form.Item>
          {editing && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}
